import React, { useState, useEffect } from 'react';
import { Form, Table } from 'react-bootstrap';

// ────────────────────────────────────────────────────────────────────────────────
// Utility helpers
// ────────────────────────────────────────────────────────────────────────────────

/**
 * "sleep_fatigue_3" → "sleep_fatigue"
 */
const domainRaw = (qName) => qName.split('_').slice(0, -1).join('_');

/**
 * Pretty‑print an underscored raw domain → "Sleep Fatigue"
 */
const prettyDomain = (raw) =>
    raw
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

// ────────────────────────────────────────────────────────────────────────────────
// TripleField — standard & scoring variants (updated to handle blank values)
// ────────────────────────────────────────────────────────────────────────────────

function TripleField(props) {
    const { question } = props;
    const eventName = props.name.toString();

    // Extract direct values from props data
    const questionName = props.name;
    const initSev = props.data && props.data[`${questionName}_severity`] != null ?
        props.data[`${questionName}_severity`] : '';
    const initFreq = props.data && props.data[`${questionName}_frequency`] != null ?
        props.data[`${questionName}_frequency`] : '';

    const [severity, setSeverity] = useState(initSev);
    const [frequency, setFrequency] = useState(initFreq);
    const [sevError, setSevError] = useState(false);
    const [freqError, setFreqError] = useState(false);

    // Calculate product whenever severity or frequency changes
    // If either field is blank, product will be 0
    const product = !sevError && !freqError && severity !== '' && frequency !== '' ?
        parseInt(severity, 10) * parseInt(frequency, 10) : 0;

    // Update local state when props data changes
    useEffect(() => {
        if (props.data) {
            const sevKey = `${questionName}_severity`;
            const freqKey = `${questionName}_frequency`;

            if (props.data[sevKey] != null && props.data[sevKey] !== severity) {
                setSeverity(props.data[sevKey]);
            }
            if (props.data[freqKey] != null && props.data[freqKey] !== frequency) {
                setFrequency(props.data[freqKey]);
            }
        }
    }, [props.data]);

    // Notify parent when values change
    useEffect(() => {
        // Always notify parent, even when fields are blank (with product=0)
        notify(severity, frequency);
    }, [severity, frequency]);

    const notify = (sev, freq) => {
        // Only include values that have been populated
        const valueToUpdate = {};

        if (sev !== '') {
            valueToUpdate[`${eventName}_severity`] = sev;
        }

        if (freq !== '') {
            valueToUpdate[`${eventName}_frequency`] = freq;
        }

        // Only calculate product if both values are valid
        if (sev !== '' && freq !== '' && !sevError && !freqError) {
            valueToUpdate[`${eventName}_product`] = parseInt(sev, 10) * parseInt(freq, 10);
        }

        const syntheticEvent = {
            target: {
                name: eventName,
                value: valueToUpdate,
            },
        };
        props.handler(syntheticEvent);
    };

    const onChangeSev = (e) => {
        const raw = e.target.value;
        setSeverity(raw);
        if (raw === '') {
            setSevError(false);
            return;
        }
        const num = parseInt(raw, 10);
        if (isNaN(num) || num < 0 || num > 3) {
            setSevError(true);
        } else {
            setSevError(false);
        }
    };

    const onChangeFreq = (e) => {
        const raw = e.target.value;
        setFrequency(raw);
        if (raw === '') {
            setFreqError(false);
            return;
        }
        const num = parseInt(raw, 10);
        if (isNaN(num) || num < 1 || num > 4) {
            setFreqError(true);
        } else {
            setFreqError(false);
        }
    };

    return (
        <>
            <td>
                <Form.Control
                    type="number"
                    name={question.severity_name}
                    value={severity}
                    min={1}
                    max={3}
                    isInvalid={sevError}
                    onChange={onChangeSev}
                    disabled={props.disabled}
                />
                <Form.Control.Feedback type="invalid">Enter a value between 1 and 3</Form.Control.Feedback>
            </td>
            <td>
                <Form.Control
                    type="number"
                    name={question.frequency_name}
                    value={frequency}
                    min={1}
                    max={4}
                    isInvalid={freqError}
                    onChange={onChangeFreq}
                    disabled={props.disabled}
                />
                <Form.Control.Feedback type="invalid">Enter a value between 1 and 4</Form.Control.Feedback>
            </td>
            <td>
                <Form.Control
                    type="number"
                    name={question.severity_frequency_name}
                    value={severity === '' || frequency === '' ? '' : product}
                    readOnly
                    disabled
                />
            </td>
        </>
    );
}

function TripleFieldScore(props) {
    const { question } = props;
    const eventName = props.name.toString();

    // Extract direct values from props data
    const questionName = props.name;
    const initSev = props.data && props.data[`${questionName}_severity`] != null ?
        props.data[`${questionName}_severity`] : '';
    const initFreq = props.data && props.data[`${questionName}_frequency`] != null ?
        props.data[`${questionName}_frequency`] : '';

    const [severity, setSeverity] = useState(initSev);
    const [frequency, setFrequency] = useState(initFreq);
    const [sevError, setSevError] = useState(false);
    const [freqError, setFreqError] = useState(false);

    // Calculate product whenever severity or frequency changes
    // If either field is blank, product will be 0
    const product = !sevError && !freqError && severity !== '' && frequency !== '' ?
        parseInt(severity, 10) * parseInt(frequency, 10) : 0;

    // Update local state when props data changes
    // useEffect(() => {
    //     if (props.data) {
    //         const sevKey = `${questionName}_severity`;
    //         const freqKey = `${questionName}_frequency`;
    //
    //         if (props.data[sevKey] != null && props.data[sevKey] !== severity) {
    //             setSeverity(props.data[sevKey]);
    //         }
    //         if (props.data[freqKey] != null && props.data[freqKey] !== frequency) {
    //             setFrequency(props.data[freqKey]);
    //         }
    //     }
    // }, [props.data]);
    useEffect(() => {
        // Only initialize the state if data is passed
        if (props.data) {
            const sevKey = `${eventName}_severity`;
            const freqKey = `${eventName}_frequency`;

            if (props.data[sevKey] != null) {
                setSeverity(props.data[sevKey]);
            }

            if (props.data[freqKey] != null) {
                setFrequency(props.data[freqKey]);
            }
        }
        // Disable this effect after the first run to prevent resetting user inputs
        // even if the parent component re-renders
        // This ensures the initialization only happens once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Notify parent when values change
    useEffect(() => {
        // Always notify parent, even when fields are blank (with product=0)
        notifyParent(severity, frequency);
    }, [severity, frequency]);

    const notifyParent = (sev, freq) => {
        const valueToUpdate = {};

        // Update severity and frequency separately
        valueToUpdate[`${eventName}_severity`] = sev !== '' ? sev : '';
        valueToUpdate[`${eventName}_frequency`] = freq !== '' ? freq : '';

        // Only calculate product if both values are valid numbers
        if (sev !== '' && freq !== '' && !sevError && !freqError) {
            const sevNum = parseInt(sev, 10);
            const freqNum = parseInt(freq, 10);

            if (!isNaN(sevNum) && !isNaN(freqNum)) {
                valueToUpdate[`${eventName}_product`] = sevNum * freqNum;
            }
        } else {
            // Set product to 0 if either is empty or invalid
            valueToUpdate[`${eventName}_product`] = 0;
        }

        const syntheticEvent = {
            target: {
                name: eventName,
                value: valueToUpdate,
            },
        };

        props.handler(syntheticEvent);
    };


    const onChangeSev = (e) => {
        const raw = e.target.value;
        setSeverity(raw);
        if (raw === '') {
            setSevError(false);
            return;
        }
        const num = parseInt(raw, 10);
        if (isNaN(num) || num < 0 || num > 3) {
            setSevError(true);
        } else {
            setSevError(false);
        }
    };

    const onChangeFreq = (e) => {
        const raw = e.target.value;
        setFrequency(raw);
        if (raw === '') {
            setFreqError(false);
            return;
        }
        const num = parseInt(raw, 10);
        if (isNaN(num) || num < 1 || num > 4) {
            setFreqError(true);
        } else {
            setFreqError(false);
        }
    };

    return (
        <>
            <td>
                <Form.Control
                    type="number"
                    name={question.severity_name}
                    value={severity}
                    min={0}
                    max={3}
                    isInvalid={sevError}
                    onChange={onChangeSev}
                    disabled={props.disabled}
                />
                <Form.Control.Feedback type="invalid">Enter a value between 0 and 3</Form.Control.Feedback>
            </td>
            <td>
                <Form.Control
                    type="number"
                    name={question.frequency_name}
                    value={frequency}
                    min={1}
                    max={4}
                    isInvalid={freqError}
                    onChange={onChangeFreq}
                    disabled={props.disabled}
                />
                <Form.Control.Feedback type="invalid">Enter a value between 1 and 4</Form.Control.Feedback>
            </td>
            <td>
                <Form.Control
                    type="number"
                    name={question.severity_frequency_name}
                    value={severity === '' || frequency === '' ? '' : product}
                    readOnly
                    disabled
                />
            </td>
        </>
    );
}

// ────────────────────────────────────────────────────────────────────────────────
// Questions_Table — now supports per‑domain scoring with proper blank handling
// ────────────────────────────────────────────────────────────────────────────────

class Questions_Table extends React.Component {
    constructor(props) {
        super(props);

        // Initialize state with existing data from props if available
        const initialValues = this.initializeRiskValues(props.data);

        this.state = {
            ...initialValues,
            ...this.computeDomainScores(initialValues),
        };
    }

    componentDidUpdate(prevProps) {
        // If data prop changes, update the state
        if (prevProps.data !== this.props.data && this.props.data) {
            const initialValues = this.initializeRiskValues(this.props.data);
            this.setState({
                ...initialValues,
                ...this.computeDomainScores(initialValues),
            });
        }
    }

    // Initialize risk values from props.data
    initializeRiskValues = (data) => {
        if (!data) return {};

        const flatValues = {};

        // Process data into flat structure
        Object.entries(data).forEach(([key, value]) => {
            // If it's already flat, just copy it
            if (!key.includes('_severity') && !key.includes('_frequency') && !key.includes('_product') && !key.includes('_score')) {
                if (typeof value !== 'object') {
                    flatValues[key] = value;
                    return;
                }
            }

            // If it's one of our flattened keys, just copy it
            if (key.includes('_severity') || key.includes('_frequency') || key.includes('_product') || key.includes('_score')) {
                flatValues[key] = value;
                return;
            }

            // Otherwise, this is one of our old nested objects that needs flattening
            if (value && typeof value === 'object') {
                if (value.severity != null) {
                    flatValues[`${key}_severity`] = value.severity;
                }
                if (value.frequency != null) {
                    flatValues[`${key}_frequency`] = value.frequency;
                }
                if (value.product != null) {
                    flatValues[`${key}_product`] = value.product;
                }
            }
        });

        return flatValues;
    };

    // ---------------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------------

    /**
     * Compute domain scores from risk values
     * Returns a flattened object with domain_score keys
     */
    computeDomainScores = (flatValues) => {
        const { questions } = this.props;
        const domainTotals = {};
        let grandTotal = 0;

        // Initialize all domains to 0
        questions.forEach(q => {
            if (q.type !== 'section') {
                const dRaw = domainRaw(q.name);
                if (!domainTotals[dRaw]) {
                    domainTotals[dRaw] = 0;
                }
            }
        });

        // Sum up products for each domain
        questions.forEach(q => {
            if (q.type !== 'section') {
                const dRaw = domainRaw(q.name);
                const productKey = `${q.name}_product`;

                // Default to 0 if the product doesn't exist or is undefined/null
                let product = 0;
                if (flatValues[productKey] != null) {
                    product = parseInt(flatValues[productKey], 10) || 0;
                }

                domainTotals[dRaw] += product;
            }
        });

        // Calculate grand total and create flattened object with domain_score keys
        const flattenedScores = {};
        Object.entries(domainTotals).forEach(([domain, total]) => {
            grandTotal += total;
            flattenedScores[`${domain}_score`] = total;
        });

        flattenedScores.grandTotal = grandTotal;

        return flattenedScores;
    };

    /**
     * Compute all totals for display
     */
    computeTotals = () => {
        // Create domain totals object from state
        const computedDomainTotals = {};

        Object.keys(this.state).forEach(key => {
            if (key.endsWith('_score')) {
                const domain = key.replace('_score', '');
                computedDomainTotals[domain] = this.state[key];
            }
        });

        return {
            domainTotals: computedDomainTotals,
            grandTotal: this.state.grandTotal || 0
        };
    };

    //--------------------------------------------------------------------------
    // Event handler — called by each TripleField component
    //--------------------------------------------------------------------------

    onChange = (event) => {
        const { name } = event.target;
        const flatValues = event.target.value; // Now only contains changed fields

        // Calculate the domain for this question
        const domain = domainRaw(name);
        const domainScoreKey = `${domain}_score`;

        // Get the old and new product values
        const oldProductKey = `${name}_product`;
        const oldProduct = this.state[oldProductKey] || 0;
        const newProduct = flatValues[`${name}_product`] || 0;

        // Calculate the change in domain score
        const currentDomainScore = this.state[domainScoreKey] || 0;
        const newDomainScore = currentDomainScore - oldProduct + newProduct;

        // Calculate new grand total
        const currentGrandTotal = this.state.grandTotal || 0;
        const newGrandTotal = currentGrandTotal - oldProduct + newProduct;

        // Update state with only the changed values and recalculated scores
        this.setState(
            (prevState) => {
                const updatedState = { ...prevState };

                // Only update fields that were actually changed
                Object.entries(flatValues).forEach(([key, value]) => {
                    updatedState[key] = value;
                });

                // Always update domain score and grand total
                updatedState[domainScoreKey] = newDomainScore;
                updatedState.grandTotal = newGrandTotal;

                return updatedState;
            },
            () => {
                // Notify the parent (NMSS component) with the complete state
                if (this.props.handler) {
                    this.props.handler(this.state);
                }
            }
        );
    };

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    render() {
        const { questions, score: useScore, disabled, data, hideHeader } = this.props;
        const { domainTotals, grandTotal } = this.computeTotals();

        // Build rows — inserting a domain‑score row whenever we hit the end of a section
        const rows = [];
        let qNumber = 0;

        questions.forEach((item, idx) => {
            // SECTION HEADER --------------------------------------------------------
            if (item.type === 'section') {
                rows.push(
                    <tr key={`sec-${item.title}`} className="table-primary">
                        <th colSpan={4}>{item.title}</th>
                    </tr>
                );
                return; // continue
            }

            // QUESTION ROW ----------------------------------------------------------
            qNumber += 1;
            rows.push(
                <tr key={item.name}>
                    <td>
                        {qNumber}. {item.question}
                    </td>
                    {useScore ? (
                        <TripleFieldScore
                            name={item.name}
                            question={item}
                            data={this.state}
                            handler={this.onChange}
                            disabled={disabled}
                        />
                    ) : (
                        <TripleField
                            name={item.name}
                            question={item}
                            data={this.state}
                            handler={this.onChange}
                            disabled={disabled}
                        />
                    )}
                </tr>
            );

            // DOMAIN TOTAL ROW ------------------------------------------------------
            const nextIsSection = idx + 1 >= questions.length || questions[idx + 1].type === 'section';
            if (nextIsSection) {
                const dRaw = domainRaw(item.name);
                const dScore = domainTotals[dRaw] || 0;
                rows.push(
                    <tr key={`score-${dRaw}`} className="table-info">
                        <th colSpan={3}>{prettyDomain(dRaw)} Score</th>
                        <td>
                            <Form.Control type="number" value={dScore} readOnly disabled className="font-weight-bold" />
                        </td>
                    </tr>
                );
            }
        });

        // GRAND TOTAL ROW ---------------------------------------------------------
        rows.push(
            <tr key="grand-total" className="table-secondary">
                <th>Total Score</th>
                <td colSpan={2}></td>
                <td>
                    <Form.Control type="number" value={grandTotal} readOnly disabled className="font-weight-bold" />
                </td>
            </tr>
        );

        return (
            <>
                <h2>Non‑Motor Symptom Assessment Scale for Parkinson's Disease</h2>
                <Table responsive bordered={false} size="md" hover striped>
                    {!hideHeader && (
                        <thead>
                        <tr>
                            <th>Question</th>
                            <th>Severity</th>
                            <th>Frequency</th>
                            <th>Severity × Frequency</th>
                        </tr>
                        </thead>
                    )}
                    <tbody>{rows}</tbody>
                </Table>
            </>
        );
    }
}

Questions_Table.defaultProps = {
    score: false,
    hideHeader: false,
    data: null,
    disabled: false,
};

export default Questions_Table;